/**
 * Human-readable labels for document type keys and messy filenames in Analytics copy.
 * Does not change API data — display-only.
 */

const ANALYTICS_DOC_TYPE_MAP = {
  medical_card: "Medical Examiner's Certificate",
  cdl: "Commercial Driver's License",
  mvr: 'Motor Vehicle Record (MVR)',
  insurance: 'Liability Insurance Certificate',
  dot_physical: 'DOT Physical',
  drug_test: "Pre-Employment Drug Test",
  clearinghouse: 'Clearinghouse Registration',
  annual_inspection: 'Annual Vehicle Inspection',
  inspection: 'Annual Vehicle Inspection',
  registration: 'Vehicle Registration',
  ifta: 'IFTA License',
  ifta_license: 'IFTA License',
  authority: 'Operating Authority',
  authority_certificate: 'Operating Authority',
  accident_register: 'Accident Register',
};

const SORTED_KEYS = Object.keys(ANALYTICS_DOC_TYPE_MAP).sort((a, b) => b.length - a.length);

export function normalizeAnalyticsWhitespace(str) {
  if (str == null) return '';
  return String(str)
    .replace(/\u00a0/g, ' ')
    .replace(/[\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function titleCaseFallback(segment) {
  const s = normalizeAnalyticsWhitespace(segment).replace(/_/g, ' ');
  if (!s) return '';
  return s
    .split(/\s+/)
    .map((w) => {
      if (!w) return w;
      const lower = w.toLowerCase();
      if (lower === 'mvr' || lower === 'dot' || lower === 'ifta' || lower === 'cdl') return lower.toUpperCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

function stripTrailingExtension(name) {
  return name.replace(/\.(png|jpe?g|pdf|jpeg)$/i, '').trim();
}

/**
 * Map a single document type key or short raw string to a label.
 */
export function getDocumentLabel(raw) {
  if (raw == null) return '';
  const normalized = normalizeAnalyticsWhitespace(raw);
  if (!normalized) return '';
  const lower = normalized.toLowerCase();
  if (ANALYTICS_DOC_TYPE_MAP[lower]) return ANALYTICS_DOC_TYPE_MAP[lower];
  const noExt = stripTrailingExtension(lower);
  if (ANALYTICS_DOC_TYPE_MAP[noExt]) return ANALYTICS_DOC_TYPE_MAP[noExt];
  const underscored = noExt.replace(/\s+/g, '_');
  if (ANALYTICS_DOC_TYPE_MAP[underscored]) return ANALYTICS_DOC_TYPE_MAP[underscored];
  let cleaned = stripTrailingExtension(normalized);
  cleaned = cleaned.replace(/_/g, ' ');
  return titleCaseFallback(cleaned);
}

/**
 * Replace document keys and clean filenames inside longer insight / analytics strings.
 */
export function formatAnalyticsDocumentDisplay(text) {
  if (text == null) return '';
  let s = normalizeAnalyticsWhitespace(text);
  if (!s) return '';

  const parenOnly = s.match(/^\s*(.+?)\s*\(([a-z0-9_]+)\)\s*$/i);
  if (parenOnly) {
    const key = parenOnly[2].toLowerCase();
    if (ANALYTICS_DOC_TYPE_MAP[key]) return ANALYTICS_DOC_TYPE_MAP[key];
  }

  let out = s;
  for (const key of SORTED_KEYS) {
    const re = new RegExp(`\\b${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    out = out.replace(re, ANALYTICS_DOC_TYPE_MAP[key]);
  }

  out = out.replace(/\s*\(([a-z0-9_]+)\)\s*$/i, (match, g1) => {
    const k = g1.toLowerCase();
    if (ANALYTICS_DOC_TYPE_MAP[k]) return '';
    return ` (${getDocumentLabel(k)})`;
  });
  out = normalizeAnalyticsWhitespace(out);

  out = out.replace(/\.(png|jpe?g|pdf|jpeg)\b/gi, '');
  out = normalizeAnalyticsWhitespace(out);

  out = out.replace(/\b[a-zA-Z][a-zA-Z0-9_]*\b/g, (token) => {
    if (!token.includes('_')) return token;
    const t = token.toLowerCase();
    if (ANALYTICS_DOC_TYPE_MAP[t]) return ANALYTICS_DOC_TYPE_MAP[t];
    return titleCaseFallback(token);
  });

  return normalizeAnalyticsWhitespace(out);
}

/**
 * Dashboard / analytics timeline entry titles: upload/update/remove patterns and doc keys.
 */
export function formatComplianceTimelineTitle(title) {
  let s = normalizeAnalyticsWhitespace(title);
  if (!s) return '';

  const labelFromPrefix = (prefix) => {
    const p = prefix.trim();
    const keyish = p.toLowerCase().replace(/\s+/g, '_');
    return getDocumentLabel(keyish) || formatAnalyticsDocumentDisplay(p) || p;
  };

  const uploadM = s.match(/^(.+?)\s+uploaded\s*:\s*(.+)$/i);
  if (uploadM) {
    const label = labelFromPrefix(uploadM[1]);
    return label ? `${label} uploaded` : formatAnalyticsDocumentDisplay(s);
  }

  const updatedM = s.match(/^(.+?)\s+updated\s*:\s*(.+)$/i);
  if (updatedM) {
    const label = labelFromPrefix(updatedM[1]);
    return label ? `${label} updated` : formatAnalyticsDocumentDisplay(s);
  }

  const removedM = s.match(/^Document\s+removed\s*:\s*(.+)$/i);
  if (removedM) {
    const rest = formatAnalyticsDocumentDisplay(removedM[1].trim());
    return rest ? `Document removed: ${rest}` : 'Document removed';
  }

  return formatAnalyticsDocumentDisplay(s);
}
